import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * InvitationGuard monitors for invitation emails in the URL.
 * When a logged-in user navigates to a URL with an invitation email parameter,
 * it shows a dialog prompting them to log out to accept the invitation.
 */
export function InvitationGuard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  const { signOut, userInfo } = useAuthStore();
  const { toast } = useToast();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  useEffect(() => {
    // If user is logged in and there's an invitation email, show logout dialog
    if (userInfo && emailParam) {
      setShowLogoutDialog(true);
    }
  }, [userInfo, emailParam]);

  const handleLogout = () => {
    try {
      signOut();
      setShowLogoutDialog(false);
      toast({
        title: 'Logged Out',
        description: 'You have been logged out. Redirecting to registration...',
      });

      // Navigate to register page with invitation email preserved
      const params = new URLSearchParams();
      if (emailParam) {
        params.set('email', emailParam);
      }
      navigate(`/register?${params.toString()}`);
    } catch (error) {
      console.error('Logout failed:', error);
      toast({
        title: 'Logout Failed',
        description: 'An error occurred while logging out. Please try again.',
        variant: 'destructive',
      });
      setShowLogoutDialog(false);
    }
  };

  const handleCancel = () => {
    setShowLogoutDialog(false);
    // Redirect to dashboard when user cancels
    navigate('/dashboard');
  };

  return (
    <AlertDialog open={showLogoutDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Accept Invitation</AlertDialogTitle>
          <AlertDialogDescription>
            You're currently logged in as <strong>{userInfo?.email}</strong>. To accept this invitation for a new
            account, you need to log out first.
            <br />
            <br />
            Would you like to log out and proceed with the invitation?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleLogout}>Log Out and Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
